/**
 * Color Token System (Hybrid Approach)
 * 
 * This file defines TypeScript color tokens that map to CSS variables.
 * Benefits:
 * - Type safety and autocomplete
 * - Runtime validation
 * - Single source of truth
 * - Easy to extend for Statsig experiments
 */

export const colorTokens = {
  // Semantic UI Colors
  background: 'var(--background)',
  foreground: 'var(--foreground)',
  
  primary: 'var(--primary)',
  'primary-foreground': 'var(--primary-foreground)',
  
  secondary: 'var(--secondary)',
  'secondary-foreground': 'var(--secondary-foreground)',
  
  accent: 'var(--accent)',
  'accent-foreground': 'var(--accent-foreground)',
  
  muted: 'var(--muted)',
  'muted-foreground': 'var(--muted-foreground)',
  
  destructive: 'var(--destructive)',
  'destructive-foreground': 'var(--destructive-foreground)',
  
  // UI Element Colors
  border: 'var(--border)',
  input: 'var(--input)',
  ring: 'var(--ring)',
  
  card: 'var(--card)',
  'card-foreground': 'var(--card-foreground)',
  
  popover: 'var(--popover)',
  'popover-foreground': 'var(--popover-foreground)',
  
  // Chart Colors
  chart: {
    1: 'var(--chart-1)',
    2: 'var(--chart-2)',
    3: 'var(--chart-3)',
    4: 'var(--chart-4)',
    5: 'var(--chart-5)',
  },
} as const;

export type ColorToken = keyof typeof colorTokens | `chart.${1 | 2 | 3 | 4 | 5}`;

/**
 * Get a color token value
 * @example getColor('primary') // 'var(--primary)'
 */
export const getColor = (token: ColorToken): string => {
  if (token.startsWith('chart.')) {
    const num = token.split('.')[1] as '1' | '2' | '3' | '4' | '5';
    return colorTokens.chart[num];
  }
  return colorTokens[token as keyof typeof colorTokens] as string;
};

/**
 * HSL Color Definition
 * Used for defining raw HSL values in themes
 */
export interface HSLColor {
  h: number; // 0-360
  s: number; // 0-100
  l: number; // 0-100
}

/**
 * Convert HSL object to CSS HSL string
 */
export const hslToString = (color: HSLColor): string => {
  return `${color.h} ${color.s}% ${color.l}%`;
};

/**
 * Validate CSS variable exists in document
 * Useful for development warnings
 */
export const validateColorToken = (token: ColorToken): boolean => {
  if (typeof window === 'undefined') return true;
  
  const value = getColor(token);
  const styles = getComputedStyle(document.documentElement);
  const cssVar = value.match(/var\((--[^)]+)\)/)?.[1];
  
  if (!cssVar) return false;
  
  const computedValue = styles.getPropertyValue(cssVar);
  return computedValue.trim() !== '';
};

/**
 * Development helper: Validate all color tokens
 */
export const validateAllColorTokens = (): {
  valid: string[];
  invalid: string[];
} => {
  if (typeof window === 'undefined') {
    return { valid: [], invalid: [] };
  }

  const valid: string[] = [];
  const invalid: string[] = [];
  
  Object.keys(colorTokens).forEach(key => {
    if (key === 'chart') return;
    
    if (validateColorToken(key as ColorToken)) {
      valid.push(key);
    } else {
      invalid.push(key);
    }
  });
  
  // Validate chart colors
  [1, 2, 3, 4, 5].forEach(num => {
    const token = `chart.${num}` as ColorToken;
    if (validateColorToken(token)) {
      valid.push(token);
    } else {
      invalid.push(token);
    }
  });
  
  return { valid, invalid };
};
